import { assertMapShellProjectionV1 } from "./map-shell-projection.js";

const UNASSIGNED_CATCH_GROUP_SLOT = 0;
const PRIZE_CATCH_GROUP_SLOT = 1;
const PINK_DOLPHIN_ITEM_ID = 820986;

function tutorialProjection(projection) {
  if (projection == null) {
    return null;
  }
  return assertMapShellProjectionV1(projection);
}

function selectedBookmarkCount(projection) {
  return Array.isArray(projection?.ui?.bookmarks?.selectedIds)
    ? projection.ui.bookmarks.selectedIds.length
    : 0;
}

function normalizedRect(rect) {
  const left = Number(rect?.left);
  const top = Number(rect?.top);
  const width = Math.max(0, Number(rect?.width) || 0);
  const height = Math.max(0, Number(rect?.height) || 0);
  const normalizedLeft = Number.isFinite(left) ? left : 0;
  const normalizedTop = Number.isFinite(top) ? top : 0;
  return {
    left: normalizedLeft,
    top: normalizedTop,
    width,
    height,
    right: normalizedLeft + width,
    bottom: normalizedTop + height,
  };
}

function intersectionArea(first, second, clearance = 0) {
  const overlapWidth = Math.max(
    0,
    Math.min(first.right, second.right + clearance) -
      Math.max(first.left, second.left - clearance),
  );
  const overlapHeight = Math.max(
    0,
    Math.min(first.bottom, second.bottom + clearance) -
      Math.max(first.top, second.top - clearance),
  );
  return overlapWidth * overlapHeight;
}

function clampCoordinate(value, minimum, maximum) {
  return Math.max(minimum, Math.min(value, maximum));
}

export function chooseTutorialCoachPosition(
  shellBounds,
  coachBounds,
  targetBounds,
  margin = 12,
  obstacleBounds = [],
  placement = "auto",
) {
  const shell = normalizedRect(shellBounds);
  const coach = normalizedRect(coachBounds);
  const targetViewport = normalizedRect(targetBounds);
  const coachWidth = Math.min(coach.width, shell.width);
  const coachHeight = Math.min(coach.height, shell.height);
  const inset = Math.max(0, Number(margin) || 0);
  const minX = Math.min(inset, Math.max(0, shell.width - coachWidth));
  const minY = Math.min(inset, Math.max(0, shell.height - coachHeight));
  const maxX = Math.max(minX, shell.width - coachWidth - inset);
  const maxY = Math.max(minY, shell.height - coachHeight - inset);
  const target = {
    left: targetViewport.left - shell.left,
    top: targetViewport.top - shell.top,
    right: targetViewport.right - shell.left,
    bottom: targetViewport.bottom - shell.top,
    width: targetViewport.width,
    height: targetViewport.height,
  };
  if (placement === "left-attached") {
    return {
      x: Math.round(
        clampCoordinate(target.left - coachWidth - inset, minX, maxX),
      ),
      y: Math.round(
        clampCoordinate(
          target.top + (target.height - coachHeight) / 2,
          minY,
          maxY,
        ),
      ),
    };
  }
  const rawCandidates = [
    { x: target.left, y: target.bottom + inset, side: "bottom" },
    { x: target.right - coachWidth, y: target.bottom + inset, side: "bottom" },
    { x: target.left, y: target.top - coachHeight - inset, side: "top" },
    { x: target.right - coachWidth, y: target.top - coachHeight - inset, side: "top" },
    {
      x: target.right + inset,
      y: target.top + (target.height - coachHeight) / 2,
      side: "right",
    },
    {
      x: target.left - coachWidth - inset,
      y: target.top + (target.height - coachHeight) / 2,
      side: "left",
    },
  ];
  const candidates = Array.from(
    new Map(
      rawCandidates.map((candidate) => {
        const clamped = {
          x: Math.round(clampCoordinate(candidate.x, minX, maxX)),
          y: Math.round(clampCoordinate(candidate.y, minY, maxY)),
          side: candidate.side,
        };
        return [`${clamped.x}:${clamped.y}`, clamped];
      }),
    ).values(),
  );
  const obstacles = Array.from(obstacleBounds || [], (bounds) => {
    const viewport = normalizedRect(bounds);
    return {
      left: viewport.left - shell.left,
      top: viewport.top - shell.top,
      right: viewport.right - shell.left,
      bottom: viewport.bottom - shell.top,
    };
  });
  const targetCenterX = target.left + target.width / 2;
  const targetCenterY = target.top + target.height / 2;
  const selected = candidates
    .map((candidate, order) => {
      const candidateRect = {
        left: candidate.x,
        top: candidate.y,
        right: candidate.x + coachWidth,
        bottom: candidate.y + coachHeight,
      };
      const coachCenterX = candidate.x + coachWidth / 2;
      const coachCenterY = candidate.y + coachHeight / 2;
      return {
        ...candidate,
        order,
        overlap: intersectionArea(candidateRect, target, inset),
        obstacleOverlap: obstacles.reduce(
          (total, obstacle) => total + intersectionArea(candidateRect, obstacle, inset),
          0,
        ),
        distance:
          (coachCenterX - targetCenterX) ** 2 +
          (coachCenterY - targetCenterY) ** 2,
      };
    })
    .sort(
      (first, second) =>
        first.overlap - second.overlap ||
        first.obstacleOverlap - second.obstacleOverlap ||
        first.distance - second.distance ||
        first.order - second.order,
    )[0];
  return { x: selected?.x ?? minX, y: selected?.y ?? minY };
}

function toolbarTarget(shell, windowId) {
  return shell?.querySelector?.(`[data-window-toggle="${windowId}"]`) || null;
}

function windowIsOpen(projection, windowId) {
  return projection?.ui?.windowUi?.[windowId]?.open !== false;
}

function windowIsPresented(shell, projection, windowId) {
  if (!windowIsOpen(projection, windowId)) {
    return false;
  }
  const root = shell?.querySelector?.(`[data-window-id="${windowId}"]`) || null;
  return root?.dataset?.mobileActive !== "false";
}

function windowIsCollapsed(projection, windowId) {
  return projection?.ui?.windowUi?.[windowId]?.collapsed === true;
}

function windowCandidateTarget(shell, projection, windowId, candidate) {
  if (!windowIsPresented(shell, projection, windowId)) {
    return toolbarTarget(shell, windowId);
  }
  const titlebar =
    shell?.querySelector?.(`[data-window-titlebar="${windowId}"]`) || null;
  if (windowIsCollapsed(projection, windowId)) {
    return titlebar;
  }
  if (!candidate || candidate.hidden) {
    return titlebar;
  }
  const style = globalThis.getComputedStyle?.(candidate);
  const bounds = candidate.getBoundingClientRect?.();
  return style?.display === "none" ||
    style?.visibility === "hidden" ||
    (bounds && (!Number(bounds.width) || !Number(bounds.height)))
    ? titlebar
    : candidate;
}

function windowTarget(shell, projection, windowId, selector) {
  return windowCandidateTarget(
    shell,
    projection,
    windowId,
    shell?.querySelector?.(selector) || null,
  );
}

function layerControlTarget(shell, projection, selector) {
  return windowTarget(shell, projection, "layers", selector);
}

function layerHoverFactTarget(shell, projection, layerId, factKey) {
  if (!windowIsPresented(shell, projection, "layers")) {
    return toolbarTarget(shell, "layers");
  }
  const titlebar =
    shell?.querySelector?.('[data-window-titlebar="layers"]') || null;
  if (windowIsCollapsed(projection, "layers")) {
    return titlebar;
  }
  const factToggle = shell?.querySelector?.(
    `input[data-layer-hover-fact-layer-id="${layerId}"][data-layer-hover-fact-key="${factKey}"]`,
  ) || null;
  if (factToggle) {
    return windowCandidateTarget(shell, projection, "layers", factToggle);
  }
  const hoverTab = shell?.querySelector?.(
    `button[data-layer-settings-layer-id="${layerId}"][data-layer-settings-tab="hover"]`,
  ) || null;
  return windowCandidateTarget(
    shell,
    projection,
    "layers",
    hoverTab || shell?.querySelector?.(
      `[data-layer-settings-toggle="${layerId}"]`,
    ) || null,
  );
}

export function firstFishPresenceTarget(shell) {
  const firstFishRow =
    shell?.querySelector?.("#fishymap-info-panel [data-fishy-loot-row]") ||
    null;
  return (
    firstFishRow?.querySelector?.(
      '[data-fishy-loot-row-item-surface] [data-fishy-provenance-channel="presence"]',
    ) || null
  );
}

export function firstFishRateTarget(shell) {
  const firstFishRow =
    shell?.querySelector?.("#fishymap-info-panel [data-fishy-loot-row]") ||
    null;
  return (
    firstFishRow?.querySelector?.(
      '[data-fishy-loot-row-item-surface] [data-fishy-provenance-channel="rate"]',
    ) || null
  );
}

export function pinkDolphinQueryTarget(shell) {
  const unit =
    shell?.querySelector?.(
      `#fishymap-query-selection [data-expression-key="fish:${PINK_DOLPHIN_ITEM_ID}"], ` +
        `#fishymap-query-selection [data-expression-key="item:${PINK_DOLPHIN_ITEM_ID}"]`,
    ) || null;
  return unit?.querySelector?.(".fishy-applied-query-unit-effect") || unit;
}

function catchProfileGroupSlot(group) {
  const slot = Number.parseInt(
    group?.getAttribute?.("data-zone-loot-group-slot"),
    10,
  );
  return Number.isInteger(slot) && slot >= 0 ? slot : null;
}

function catchProfileGroups(shell) {
  return Array.from(
    shell?.querySelectorAll?.(
      "#fishymap-info-panel .fishymap-zone-loot-group",
    ) || [],
  );
}

function preferredCatchProfileGroup(shell, { requireRow = false } = {}) {
  const groups = catchProfileGroups(shell).filter(
    (group) => !requireRow || group.querySelector?.("[data-fishy-loot-row]"),
  );
  return (
    groups.find(
      (group) => catchProfileGroupSlot(group) === PRIZE_CATCH_GROUP_SLOT,
    ) ||
    groups.find((group) => {
      const slot = catchProfileGroupSlot(group);
      return slot !== null && slot > UNASSIGNED_CATCH_GROUP_SLOT;
    }) ||
    groups.find(
      (group) => catchProfileGroupSlot(group) === UNASSIGNED_CATCH_GROUP_SLOT,
    ) ||
    groups[0] ||
    null
  );
}

export function catchProfileGroupTarget(shell) {
  return (
    preferredCatchProfileGroup(shell)?.querySelector?.(
      ".fishymap-zone-loot-group-header",
    ) || null
  );
}

function preferredCatchProfileRow(shell) {
  return (
    preferredCatchProfileGroup(shell, { requireRow: true })?.querySelector?.(
      "[data-fishy-loot-row]",
    ) || null
  );
}

export function catchProfileItemTarget(shell) {
  return (
    preferredCatchProfileRow(shell)?.querySelector?.(
      "[data-fishy-loot-row-item-surface]",
    ) || null
  );
}

export function catchProfileRateTarget(shell) {
  return (
    preferredCatchProfileRow(shell)?.querySelector?.(
      "[data-fishy-loot-row-leading-metric]",
    ) || null
  );
}

export function tutorialTaskStartScrollTop(taskList, task) {
  if (!taskList || !task) {
    return 0;
  }
  const currentScrollTop = Math.max(0, Number(taskList.scrollTop) || 0);
  const listBounds = taskList.getBoundingClientRect?.();
  const taskBounds = task.getBoundingClientRect?.();
  if (
    Number.isFinite(Number(listBounds?.top)) &&
    Number.isFinite(Number(taskBounds?.top))
  ) {
    return Math.max(
      0,
      currentScrollTop + Number(taskBounds.top) - Number(listBounds.top),
    );
  }
  return Math.max(0, Number(task.offsetTop) || 0);
}

export function tutorialGuidanceScrollTop(taskList, guidance) {
  if (!taskList || !guidance) {
    return 0;
  }
  const currentScrollTop = Math.max(0, Number(taskList.scrollTop) || 0);
  const listBounds = taskList.getBoundingClientRect?.();
  const guidanceBounds = guidance.getBoundingClientRect?.();
  const listTop = Number(listBounds?.top);
  const listBottom = Number(listBounds?.bottom);
  const guidanceTop = Number(guidanceBounds?.top);
  const guidanceBottom = Number(guidanceBounds?.bottom);
  if (
    Number.isFinite(listTop) &&
    Number.isFinite(listBottom) &&
    Number.isFinite(guidanceTop) &&
    Number.isFinite(guidanceBottom)
  ) {
    if (guidanceBottom > listBottom) {
      return currentScrollTop + guidanceBottom - listBottom;
    }
    if (guidanceTop < listTop) {
      return Math.max(0, currentScrollTop + guidanceTop - listTop);
    }
    return currentScrollTop;
  }
  return Math.max(
    0,
    (Number(guidance.offsetTop) || 0) +
      (Number(guidance.offsetHeight) || 0) -
      (Number(taskList.clientHeight) || 0),
  );
}

export function tutorialVignetteHoleRect(
  shellBounds,
  targetBounds,
  padding = 10,
) {
  const shell = normalizedRect(shellBounds);
  const target = normalizedRect(targetBounds);
  const inset = Math.max(0, Number(padding) || 0);
  const holeLeft = clampCoordinate(
    target.left - shell.left - inset,
    0,
    shell.width,
  );
  const holeTop = clampCoordinate(
    target.top - shell.top - inset,
    0,
    shell.height,
  );
  const holeRight = clampCoordinate(
    target.right - shell.left + inset,
    holeLeft,
    shell.width,
  );
  const holeBottom = clampCoordinate(
    target.bottom - shell.top + inset,
    holeTop,
    shell.height,
  );
  return Object.freeze({
    left: holeLeft,
    top: holeTop,
    width: holeRight - holeLeft,
    height: holeBottom - holeTop,
  });
}

export function tutorialActionTarget(
  action,
  shell,
  progress = {},
  projection = shell?.mapShellProjection,
) {
  const value = tutorialProjection(projection);
  if (!value) {
    return null;
  }
  switch (String(action ?? "")) {
    case "apply-pink-dolphin":
    case "apply-sea-bunny":
    case "apply-same-fish-query":
    case "apply-separate-fish-query":
      return windowTarget(shell, value, "search", "#fishymap-query-surface");
    case "confirm-pink-dolphin":
      return windowCandidateTarget(
        shell,
        value,
        "search",
        pinkDolphinQueryTarget(shell),
      );
    case "clear-query":
      return windowTarget(shell, value, "search", "#fishymap-query-clear");
    case "focus-query":
      return windowTarget(shell, value, "search", "#fishymap-focus-search");
    case "cycle-query-window":
      return toolbarTarget(shell, "search");
    case "cycle-layer-collapse":
    case "move-layer-window":
      return windowIsPresented(shell, value, "layers")
        ? shell?.querySelector?.('[data-window-titlebar="layers"]') || null
        : toolbarTarget(shell, "layers");
    case "select-map-point":
    case "inspect-hotspot":
    case "inspect-region-hover":
      return shell?.querySelector?.("#bevy") || null;
    case "inspect-catch-group":
      if (
        shell?.querySelector?.("#fishymap-info-panel")?.getAttribute?.(
          "data-active-pane-id",
        ) !== "zone"
      ) {
        return windowTarget(
          shell,
          value,
          "zoneInfo",
          '[data-zone-info-tab="zone"]',
        );
      }
      return windowCandidateTarget(
        shell,
        value,
        "zoneInfo",
        catchProfileGroupTarget(shell),
      );
    case "inspect-catch-item":
      return windowCandidateTarget(
        shell,
        value,
        "zoneInfo",
        catchProfileItemTarget(shell),
      );
    case "inspect-catch-rate":
      return windowCandidateTarget(
        shell,
        value,
        "zoneInfo",
        catchProfileRateTarget(shell),
      );
    case "inspect-presence-provenance":
    case "inspect-rate-provenance":
      if (!windowIsPresented(shell, value, "zoneInfo")) {
        return toolbarTarget(shell, "zoneInfo");
      }
      if (windowIsCollapsed(value, "zoneInfo")) {
        return (
          shell?.querySelector?.('[data-window-titlebar="zoneInfo"]') || null
        );
      }
      return action === "inspect-rate-provenance"
        ? firstFishRateTarget(shell)
        : firstFishPresenceTarget(shell);
    case "select-hotspot-tab":
      return windowTarget(
        shell,
        value,
        "zoneInfo",
        shell?.querySelector?.("#fishymap-info-panel")?.getAttribute?.(
          "data-active-pane-id",
        ) === "hotspot"
          ? "#fishymap-zone-info-panel"
          : '[data-zone-info-tab="hotspot"]',
      );
    case "show-hotspots-layer":
      if (progress.hotspotsFilteredByQuery === true) {
        return windowTarget(shell, value, "search", "#fishymap-query-clear");
      }
      return layerControlTarget(
        shell,
        value,
        '[data-layer-visibility="hotspots"]',
      );
    case "hide-hotspots-layer":
      return layerControlTarget(
        shell,
        value,
        '[data-layer-visibility="hotspots"]',
      );
    case "cycle-fish-evidence-settings":
    case "close-fish-evidence-settings":
      return layerControlTarget(
        shell,
        value,
        '[data-layer-settings-toggle="fish_evidence"]',
      );
    case "change-fish-evidence-opacity":
      return layerControlTarget(
        shell,
        value,
        'input[data-layer-opacity="fish_evidence"]',
      );
    case "place-bookmark":
      if (!windowIsPresented(shell, value, "bookmarks")) {
        return toolbarTarget(shell, "bookmarks");
      }
      return value.ui?.bookmarks?.placing === true
        ? shell?.querySelector?.("#bevy") || null
        : shell?.querySelector?.("#fishymap-bookmark-place") || null;
    case "activate-bookmark":
      if (!windowIsPresented(shell, value, "bookmarks")) {
        return toolbarTarget(shell, "bookmarks");
      }
      if (windowIsCollapsed(value, "bookmarks")) {
        return (
          shell?.querySelector?.('[data-window-titlebar="bookmarks"]') || null
        );
      }
      return (
        Array.from(
          shell?.querySelectorAll?.("button[data-bookmark-activate]") || [],
        ).find(
          (button) =>
            String(
              button.getAttribute?.("data-bookmark-activate") || "",
            ).trim() === String(progress.createdBookmarkId || "").trim(),
        ) || null
      );
    case "copy-details-bookmark":
      return windowTarget(
        shell,
        value,
        "zoneInfo",
        shell?.querySelector?.("#fishymap-info-panel")?.getAttribute?.(
          "data-active-pane-id",
        ) === "territory"
          ? "#fishymap-zone-info-status"
          : '[data-zone-info-tab="territory"]',
      );
    case "export-bookmarks":
      if (!windowIsPresented(shell, value, "bookmarks")) {
        return toolbarTarget(shell, "bookmarks");
      }
      return windowTarget(
        shell,
        value,
        "bookmarks",
        selectedBookmarkCount(value) > 0
          ? '[data-bookmark-tutorial-actions="copy-export"]'
          : "input[data-bookmark-select]",
      );
    case "import-bookmarks":
      return windowTarget(
        shell,
        value,
        "bookmarks",
        '[data-bookmark-tutorial-actions="import-paste"]',
      );
    case "open-map-presets":
      return windowTarget(
        shell,
        value,
        "settings",
        'fishy-preset-manager[data-preset-collection="map-presets"] [data-role="open"]',
      );
    case "clone-map-preset":
      return windowTarget(
        shell,
        value,
        "settings",
        'fishy-preset-manager[data-preset-collection="map-presets"] [data-action="clone-selected"]',
      );
    case "rename-map-preset":
      return windowTarget(
        shell,
        value,
        "settings",
        'fishy-preset-manager[data-preset-collection="map-presets"] [data-role="selected-name"]',
      );
    case "enable-origin-hover-fact":
      return layerHoverFactTarget(
        shell,
        value,
        "regions",
        "origin_region",
      );
    case "enable-resources-hover-fact":
      return layerHoverFactTarget(
        shell,
        value,
        "region_groups",
        "resource_group",
      );
    case "detach-regions-clip":
    case "attach-regions-to-zone-mask":
      return layerControlTarget(
        shell,
        value,
        '[data-layer-settings-toggle="regions"]',
      );
    default:
      return null;
  }
}

export function tutorialTargetScrollState(target) {
  const scrollContainer = target?.closest?.("#fishymap-layers-body") || null;
  const targetBounds = target?.getBoundingClientRect?.();
  const containerBounds = scrollContainer?.getBoundingClientRect?.();
  if (
    !scrollContainer ||
    !targetBounds ||
    !containerBounds ||
    Number(targetBounds.height) <= 0 ||
    Number(containerBounds.height) <= 0
  ) {
    return { direction: "", presentationTarget: target };
  }
  if (Number(targetBounds.bottom) > Number(containerBounds.bottom)) {
    return { direction: "down", presentationTarget: scrollContainer };
  }
  if (Number(targetBounds.top) < Number(containerBounds.top)) {
    return { direction: "up", presentationTarget: scrollContainer };
  }
  return { direction: "", presentationTarget: target };
}

export function tutorialObstacleBounds(shell, target, coach) {
  const targetWindow = target?.closest?.("[data-window-id]") || null;
  return Array.from(
    shell?.querySelectorAll?.(
      "[data-window-id], #fishymap-toolbar, #fishymap-view-controls",
    ) || [],
  )
    .filter((element) => {
      if (element === coach || element === targetWindow || element.hidden) {
        return false;
      }
      const style = globalThis.getComputedStyle?.(element);
      return style?.display !== "none" && style?.visibility !== "hidden";
    })
    .map((element) => element.getBoundingClientRect?.())
    .filter((rect) => Number(rect?.width) > 0 && Number(rect?.height) > 0);
}
